// src/sheets/AddPlantForm.jsx
// Phase 37 Plan 05 — extracted from src/App.jsx (lines 6647-7116, ~470 lines).
//
// Scope: plant-add form with two-step lookup ladder — static-DB first
//        (zero-cost, all tiers), AI second (premium-only). Renders a search
//        autocomplete, an unknown-plant fallback chip row, and a confirmation
//        card with garden space / location / quantity / "more details"
//        disclosure. Consumed by both App() (Plants view AddPlantSheet) and
//        OnboardingPlant (last onboarding scene before Reveal). The hosting
//        component supplies aiStatusSetters so lookup failures can update the
//        global aiStatus banner via reprobeAiStatus.
//
// Default-export per the new feature-folder convention (CLAUDE.md rule 1).

import { useState, useRef, useEffect } from 'react';
import { ClearableInput } from '../App.jsx';   // tiny atom (D-01)
import { fetchWikiImage } from '../lib/wiki-image.js';
import {
  parseSchedule, parseColour, callAI, richDefaultTasks,
  lookupStaticSchedule, reprobeAiStatus,
} from '../lib/ai.js';
import { PLANT_SUGGESTIONS } from '../data/suggestions.js';
import { PLANT_TYPES } from '../data/plant-types.js';
import { UpgradePromptHost } from '../home-panel/UpgradePrompt.jsx';
import OnboardingChip from '../onboarding/OnboardingChip.jsx';

export default function AddPlantForm({plant,onChange,onAdd,onCancel,areas,aiReady,features,upgradeProps,compact=false,aiStatus='ready',onAiFailure,aiStatusSetters}) {
  // Phase 34 / RES-01 / D-04 — setters bundle wired down from App so the
  // lookupSchedule failure paths can call reprobeAiStatus(...) directly.
  const {setServerKeyReady, setAiStatus, setLastAiFailureAt} = aiStatusSetters || {};
  const set=(k,v)=>onChange(prev=>({...prev,[k]:v}));
  const selArea=areas.find(a=>a.id===plant?.area);
  const nameStr=plant?.name||"";

  const [lookupState,setLookupState]=useState("idle"); // idle | loading | done | error
  const [schedulePreview,setSchedulePreview]=useState(null); // parsed tasks object
  const [colourPreview,setColourPreview]=useState(null);    // parsed colour object
  const [scheduleNote,setScheduleNote]=useState("");
  const [sizePreview,setSizePreview]=useState(null); // {height,spread} from AI
  const [showUnknownPlantBanner,setShowUnknownPlantBanner]=useState(false); // unknownPlant trigger
  const [showDetails,setShowDetails]=useState(false); // D-11 More details disclosure
  // #204 D-04.1 — Image preview for the green name-match confirmation box.
  // null  = loading (render skeleton)
  // ''    = explicit failure / no wiki-or-latin (render nothing)
  // 'url' = resolved (render <img>)
  const [matchImageSrc,setMatchImageSrc]=useState('');

  // Autocomplete state
  const [dropdownItems,setDropdownItems]=useState([]);
  const [showDropdown,setShowDropdown]=useState(false);
  const [activeIdx,setActiveIdx]=useState(-1);
  const nameInputRef=useRef(null);
  const nameWrapperRef=useRef(null);
  // Debounce timer ref for unknownPlant trigger (150ms after blur)
  const unknownPlantTimerRef=useRef(null);
  // Phase 34 / RES-01 / D-03 — tracks whether the most recent lookupSchedule()
  // call ended on the fallback (no API key, AI non-2xx, AI parse failure, or
  // network exception). Read by handleAdd() to stamp addedVia:'fallback-default'.
  // Ref (not state) so the value isn't captured in stale closures and a
  // late-arriving change doesn't trigger a re-render.
  const usedFallbackRef=useRef(false);

  // #204 D-04.1 — Resolve the Wikipedia image for the matched plant whenever
  // the typeahead updates plant.wiki/latin. Pattern matches PlantImage's
  // state-then-effect-then-fetch (src/App.jsx:1894).
  useEffect(()=>{
    const w = plant?.wiki;
    const l = plant?.latin;
    if (!w && !l) { setMatchImageSrc(''); return; }
    setMatchImageSrc(null); // loading — render skeleton
    let cancelled = false;
    fetchWikiImage(w, l)
      .then(url => { if (!cancelled) setMatchImageSrc(url || ''); })
      .catch(() => { if (!cancelled) setMatchImageSrc(''); });
    return () => { cancelled = true; };
  },[plant?.wiki, plant?.latin]);

  useEffect(()=>{
    function handleMouseDown(e){
      if (nameWrapperRef.current&&!nameWrapperRef.current.contains(e.target)){
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown",handleMouseDown);
    return ()=>document.removeEventListener("mousedown",handleMouseDown);
  },[]);

  function handleNameChange(val){
    // D-09: user typing clears any prior lookup so default view resurfaces
    if (lookupState!=="idle") setLookupState("idle");
    if (schedulePreview) setSchedulePreview(null);
    if (scheduleNote) setScheduleNote("");
    // Phase 34 / RES-01 / D-03 — clear the fallback flag when the user
    // starts a new name; the next lookup decides freshly.
    usedFallbackRef.current = false;
    set("name",val);
    if (val.length<2){ setShowDropdown(false); setDropdownItems([]); return; }
    const lower=val.toLowerCase();
    const matches=PLANT_SUGGESTIONS.filter(p=>
      p.name.toLowerCase().includes(lower)||p.latin.toLowerCase().includes(lower)
    ).slice(0,8);
    setDropdownItems(matches);
    setShowDropdown(true);
    setActiveIdx(-1);
  }

  function selectSuggestion(s){
    onChange(prev=>({...prev,name:s.name,latin:s.latin,type:s.type,wiki:s.wiki}));
    setShowDropdown(false);
    setDropdownItems([]);
    setActiveIdx(-1);
    lookupSchedule({name:s.name,latin:s.latin,type:s.type});
  }

  function handleKeyDown(e){
    if (!showDropdown||dropdownItems.length===0) return;
    if (e.key==="ArrowDown"){
      e.preventDefault();
      setActiveIdx(i=>i<dropdownItems.length-1?i+1:0);
    } else if (e.key==="ArrowUp"){
      e.preventDefault();
      setActiveIdx(i=>i>0?i-1:dropdownItems.length-1);
    } else if (e.key==="Enter"){
      if (activeIdx>=0){
        e.preventDefault();
        selectSuggestion(dropdownItems[activeIdx]);
      } else if (dropdownItems.length>0){
        e.preventDefault();
        selectSuggestion(dropdownItems[0]);
      }
    } else if (e.key==="Escape"){
      setShowDropdown(false);
    }
  }

  async function lookupSchedule(overrides={}) {
    // M-01: destructure once with safe defaults so the rest of the function is null-safe
    const {name: pName = "", latin: pLatin = "", type: pType = ""} = plant || {};
    const name=(overrides.name||pName).trim()||(overrides.latin||pLatin).trim();
    const latin=overrides.latin!==undefined?overrides.latin:pLatin;
    const type=overrides.type!==undefined?overrides.type:pType;
    if (!name) { setScheduleNote("Enter a plant name first."); return; }
    // Phase 34 / RES-01 / D-03 — fresh attempt, reset fallback flag.
    usedFallbackRef.current = false;

    // Step 1: Static database — all tiers, zero AI cost (D-08, D-10)
    const staticData = lookupStaticSchedule(name, latin);
    if (staticData) {
      setSchedulePreview(staticData.tasks);
      if (Object.keys(staticData.colour).length > 0) setColourPreview(staticData.colour);
      if (staticData.height) setSizePreview({ height: staticData.height, spread: staticData.spread });
      setScheduleNote('Schedule loaded from plant database.');
      setLookupState('done');
      return;
    }

    // Step 2: AI — premium users only, when static DB has no entry (D-09, D-10)
    if (!aiReady || !features?.ai_schedule) {
      // Phase 34 / RES-01 / D-03 — explicit: NOT a fallback-default in this case.
      // The user never had AI access; richDefaultTasks is the intended path,
      // not a recovery from outage. addedVia stays absent.
      setScheduleNote(features?.ai_schedule
        ? 'No API key configured — using smart defaults instead.'
        : 'Using smart defaults — upgrade to Premium for AI-generated schedules.');
      setSchedulePreview(richDefaultTasks(type, overrides.name||pName, latin));
      setLookupState("done");
      return;
    }
    setLookupState("loading");
    setScheduleNote("");
    try {
      const prompt=`You are an expert UK gardener. Give me a detailed month-by-month maintenance schedule for ${name}${latin?" ("+latin+")":""} grown in a UK garden${type?", plant type: "+type:""}.

Start your response with two lines (before the monthly schedule):
Height: [typical mature height in metres, e.g. 1.5]
Spread: [typical mature spread in metres, e.g. 0.6]

Then give the monthly schedule as a list organised by month. Use this exact format — month name followed by a colon on its own line, then bullet points:

March:
- Prune back hard to encourage new growth
- Apply balanced slow-release fertiliser

April:
- Begin regular watering as growth accelerates

Include: pruning, feeding, watering, mulching, dividing, deadheading, and seasonal information. Be specific and practical for UK conditions. Only include months that need action — skip months with nothing to do.

After the monthly schedule, add a section:

## Seasonal Colour
For each month where this plant has notable colour or visual interest, one line per entry:
[month number]: [type], [hex colour], [brief label]
Type must be one of: flower, foliage, fruit, bark, other
e.g.
5: flower, #FF91B0, Pink blooms
10: foliage, #D4691E, Autumn colour
Only include months with real colour interest — skip months with nothing notable.`;

      const result=await callAI({model:"claude-haiku-4-5-20251001",max_tokens:1500,messages:[{role:"user",content:prompt}]});
      if(!result.ok){
        // Phase 34 / RES-01 / D-04 — fire-and-forget reprobe so the global banner
        // updates if this is the first sign of an outage.
        reprobeAiStatus(setServerKeyReady, setAiStatus, setLastAiFailureAt);
        setScheduleNote("Could not fetch schedule — using smart defaults instead.");
        setSchedulePreview(richDefaultTasks(pType, pName, pLatin));
        usedFallbackRef.current = true; // Phase 34 / RES-01 / D-03
        setLookupState("done");
        return;
      }
      const text=result.data.content?.[0]?.text||"";
      const parsed=parseSchedule(text);
      const monthCount=Object.keys(parsed).length;
      if(monthCount===0){
        setScheduleNote("Could not parse schedule — using smart defaults instead.");
        setSchedulePreview(richDefaultTasks(pType, pName, pLatin));
        usedFallbackRef.current = true; // Phase 34 / RES-01 / D-03 — AI parse failure counts as fallback
        setLookupState("done");
        return;
      }
      // AI succeeded — reset fallback flag in case a prior attempt set it.
      usedFallbackRef.current = false;
      // Extract height/spread from response header lines
      const hMatch=text.match(/^Height:\s*([\d.]+)/im);
      const sMatch=text.match(/^Spread:\s*([\d.]+)/im);
      const aiH=hMatch?parseFloat(hMatch[1]):null;
      const aiS=sMatch?parseFloat(sMatch[1]):null;
      if (aiH||aiS) {
        setSizePreview({height:aiH,spread:aiS});
        onChange(prev=>({...prev,...(aiH?{height:String(aiH)}:{}),...(aiS?{spread:String(aiS)}:{})}));
      }
      const parsedColour=parseColour(text);
      if(Object.keys(parsedColour).length>0) setColourPreview(parsedColour);
      setSchedulePreview(parsed);
      setScheduleNote(`✓ Found ${monthCount}-month schedule from AI — review below then add to Tend`);
      setLookupState("done");
    } catch(e) {
      // Phase 34 / RES-01 / D-04 — network exception during AI fetch; fire-and-forget
      // reprobe so global aiStatus banner reflects the outage.
      reprobeAiStatus(setServerKeyReady, setAiStatus, setLastAiFailureAt);
      setScheduleNote("Could not fetch schedule — using smart defaults instead.");
      setSchedulePreview(richDefaultTasks(pType, pName, pLatin));
      usedFallbackRef.current = true; // Phase 34 / RES-01 / D-03
      setLookupState("done");
    }
  }

  const handleAdd=()=>{
    // Phase 34 / RES-01 / D-03 — surface the fallback provenance so addPlant can
    // stamp addedVia: 'fallback-default' on the new plant and the expanded card
    // shows the "we used a default schedule" note.
    onAdd(schedulePreview||null, colourPreview||{}, { usedFallback: !!usedFallbackRef.current });
  };

  // D-09..D-14: single-column simplified form — confirmation card appears after lookup
  // Plant type enum imported from src/data/plant-types.js (20-entry frozen list, D-06)
  const titleCase=(s)=>s.split(" ").map(w=>w[0].toUpperCase()+w.slice(1)).join(" ");
  const trimmedName=nameStr.trim();
  // H-02: gate on explicit post-lookup state (lookupState or a non-empty plant.tasks).
  // Inferring from plant?.type would flip this true too eagerly for callers that
  // pre-seed a type but no schedule.
  const hasLookupResult = !!trimmedName && (
    lookupState === "done" ||
    (plant?.tasks && Object.keys(plant.tasks).length > 0)
  );
  const unknownName = !hasLookupResult && trimmedName.length>0 &&
    !PLANT_SUGGESTIONS.some(s=>s.name.toLowerCase()===trimmedName.toLowerCase());
  // Unknown-plant fallback path: name typed, no suggestion match, no type yet
  const showUnknownPath = unknownName;
  // UAT gap 7: unknown plant must pick a type before CTA activates
  const needsTypeChoice = showUnknownPath && !hasLookupResult;
  // Field style helper (keeps consistent look with previous form)
  // UAT2-GAP-07 (Phase 31.1 Plan 11): 16px iOS zoom floor via --font-input token.
  const fieldStyle={padding:"8px 12px",borderRadius:8,border:"1px solid var(--c-border)",fontSize:'var(--font-input)',width:"100%",boxSizing:"border-box",minWidth:0};
  const labelStyle={fontSize:'0.8125rem',color:"var(--c-text-2)",fontWeight:600};
  const fieldWrap={display:"flex",flexDirection:"column",gap:5};

  const inputId=(key)=>`addplant-${key}`;

  return(
    <div style={{display:"flex",flexDirection:"column",gap:16,maxWidth:480,margin:"0 auto",width:"100%",background:"var(--c-bg)",borderRadius:14,padding:20,marginBottom:18,border:"1px solid var(--c-border)",boxShadow:"0 2px 10px rgba(45,90,45,0.08)",boxSizing:"border-box"}}>
      <h3 style={{fontFamily:"var(--font-family-display)",color:"var(--c-heading)",margin:0,fontSize:"var(--font-h3)"}}>Add new plant</h3>

      {/* D-09: default view — Common name only */}
      <div style={fieldWrap}>
        <label htmlFor={inputId("name")} style={labelStyle}>Common name *</label>
        <div ref={nameWrapperRef} style={{position:"relative"}}>
          <input
            id={inputId("name")}
            ref={nameInputRef}
            value={nameStr}
            onChange={e=>handleNameChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={()=>nameStr.length>=2&&dropdownItems.length>0&&setShowDropdown(true)}
            onBlur={()=>{
              // unknownPlant trigger: 150ms debounce after blur, fires when name≥2 and no suggestions
              clearTimeout(unknownPlantTimerRef.current);
              unknownPlantTimerRef.current=setTimeout(()=>{
                if(nameStr.length>=2&&dropdownItems.length===0&&upgradeProps?.shouldShow('unknownPlant')){
                  setShowUnknownPlantBanner(true);
                }
              },150);
            }}
            placeholder="e.g. English Lavender"
            style={{...fieldStyle}}
          />
          {showDropdown&&dropdownItems.length>0&&(
            <div style={{
              position:"absolute",top:"100%",left:0,right:0,zIndex:200,
              background:"var(--c-bg)",border:"1px solid var(--c-border)",borderRadius:10,
              boxShadow:"0 4px 20px rgba(0,0,0,0.12)",marginTop:4,maxHeight:300,overflowY:"auto"
            }}>
              {dropdownItems.map((s,i)=>(
                <div
                  key={i}
                  onMouseDown={e=>{e.preventDefault();selectSuggestion(s);}}
                  style={{
                    padding:"10px 14px",cursor:"pointer",
                    background:i===activeIdx?"var(--c-tint)":"transparent",
                    borderBottom:i<dropdownItems.length-1?"1px solid var(--c-border)":"none"
                  }}
                >
                  <div style={{fontWeight:600,fontSize:'0.875rem',color:"var(--c-heading)"}}>{s.name}</div>
                  <div style={{fontSize:'0.75rem',color:"var(--c-text-3)",fontStyle:"italic"}}>{s.latin} · {s.type}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* D-12: unknown-plant fallback — chip row + upgrade banner */}
      {showUnknownPath && (
        <>
          {upgradeProps?.shouldShow && upgradeProps.shouldShow('unknownPlant') && (
            <UpgradePromptHost key="unknownPlant" triggerId="unknownPlant" variant="inline-banner"
              title="Plant not found in our database"
              body="We don't have growing data for this plant yet. Premium uses AI to add any plant you name."
              primaryLabel="Go Premium"
              onPrimary={()=>{ upgradeProps.onPrimary('unknownPlant'); setShowUnknownPlantBanner(false); }}
              onDismiss={()=>{ upgradeProps.onDismiss('unknownPlant'); setShowUnknownPlantBanner(false); }}
              onShow={()=>upgradeProps.onShow('unknownPlant')}/>
          )}
          <div style={fieldWrap}>
            <label style={labelStyle}>Pick a plant type to continue *</label>
            <div style={{fontSize:'0.8125rem',color:"var(--c-text-2)",lineHeight:1.5}}>
              We don't recognise this plant, so we need to know roughly what it is to set up a sensible care schedule.
            </div>
            <div style={{display:"flex",flexWrap:"wrap",gap:8,marginTop:4}}>
              {PLANT_TYPES.map((t)=>(
                <OnboardingChip
                  key={t.id}
                  label={t.label}
                  selected={plant?.type===t.id}
                  onClick={()=>{
                    const tasks=richDefaultTasks(t.id, nameStr, plant?.latin||"");
                    onChange({...(plant||{}),type:t.id,tasks});
                    setSchedulePreview(tasks);
                    setLookupState("done");
                    setScheduleNote("Schedule set from smart defaults.");
                  }}
                />
              ))}
            </div>
          </div>
        </>
      )}

      {/* D-10, D-11: confirmation card — shows after a lookup result (selection or chip click) */}
      {hasLookupResult && (
        <>
          {/* Plant summary — #204 adds an image slot beside the copy when we have a Wikipedia image */}
          <div style={{padding:"12px 14px",borderRadius:10,background:"var(--c-tint)",border:"1px solid var(--c-border)",display:"flex",alignItems:"center",gap:12}}>
            {matchImageSrc === null && (
              <div data-testid="match-image-skeleton"
                aria-label="Loading image"
                style={{width:48,height:48,borderRadius:8,background:"var(--c-border)",flexShrink:0}} />
            )}
            {typeof matchImageSrc === 'string' && matchImageSrc.length > 0 && (
              <img src={matchImageSrc} alt={plant?.name || ''}
                width={48} height={48}
                style={{width:48,height:48,borderRadius:8,objectFit:"cover",flexShrink:0}} />
            )}
            {/* matchImageSrc === '' → render nothing per D-04.1 (no broken-image icon) */}
            <div className="flex-min">
              <div style={{fontFamily:"var(--font-family-display)",fontWeight:600,fontSize:"var(--font-h3)",color:"var(--c-heading)"}}>{plant?.name}</div>
              {plant?.latin && (
                <div style={{fontStyle:"italic",fontSize:'0.8125rem',color:"var(--c-text-3)",marginTop:2}}>{plant.latin}</div>
              )}
              {plant?.type && (
                <span style={{display:"inline-block",marginTop:6,padding:"2px 10px",borderRadius:12,background:"var(--c-bg)",border:"1px solid var(--c-border)",fontSize:'0.75rem',color:"var(--c-heading)",fontWeight:600}}>{titleCase(plant.type)}</span>
              )}
            </div>
          </div>

          {/* Inline Area + Count (D-10) */}
          <div style={fieldWrap}>
            <label htmlFor={inputId("area")} style={labelStyle}>Garden space</label>
            <select id={inputId("area")} value={plant?.area||""} onChange={e=>set("area",e.target.value)} style={fieldStyle}>
              {areas.map(a=><option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
          {/* Location (sub-area) — promoted onto confirmation card per UAT gap 4.
              Gated on the selected Growing Space having at least one sub-area so we don't show a dead control. */}
          {selArea?.subAreas?.length > 0 && (
            <div style={fieldWrap}>
              <label htmlFor={inputId("subArea")} style={labelStyle}>Location</label>
              <select id={inputId("subArea")} value={plant?.subArea||""} onChange={e=>set("subArea",e.target.value)} style={fieldStyle}>
                <option value="">— none —</option>
                {selArea.subAreas.map(s=><option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          )}
          <div style={fieldWrap}>
            <label htmlFor={inputId("count")} style={labelStyle}>Quantity</label>
            <input id={inputId("count")} type="number" min={1} value={plant?.count??1} onChange={e=>set("count",e.target.value)} style={fieldStyle}/>
          </div>

          {/* D-11: More details disclosure */}
          <div>
            <button
              type="button"
              onClick={()=>setShowDetails(v=>!v)}
              aria-expanded={showDetails}
              style={{background:"transparent",border:"none",color:"var(--c-heading)",fontSize:'0.8125rem',fontWeight:600,cursor:"pointer",padding:"6px 0"}}
            >
              {showDetails?"▴ Hide details":"▾ More details"}
            </button>
          </div>
          {showDetails && (
            <div style={{display:"flex",flexDirection:"column",gap:12,padding:"8px 0"}}>
              <div style={fieldWrap}>
                <label htmlFor={inputId("cultivar")} style={labelStyle}>Cultivar / variety</label>
                <ClearableInput id={inputId("cultivar")} value={plant?.cultivar||""} onChange={e=>set("cultivar",e.target.value)} placeholder="e.g. 'Fastigiata'" style={fieldStyle}/>
              </div>
              <div style={fieldWrap}>
                <label htmlFor={inputId("latin")} style={labelStyle}>Latin name</label>
                <ClearableInput id={inputId("latin")} value={plant?.latin||""} onChange={e=>set("latin",e.target.value)} placeholder="e.g. Taxus baccata" style={fieldStyle}/>
              </div>
              <div style={fieldWrap}>
                <label htmlFor={inputId("type")} style={labelStyle}>Plant type</label>
                <select id={inputId("type")} value={plant?.type||""} onChange={e=>set("type",e.target.value)} style={fieldStyle}>
                  {PLANT_TYPES.map(t=><option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
              </div>
              <div style={fieldWrap}>
                <label htmlFor={inputId("height")} style={labelStyle}>Height (m) <span style={{fontWeight:400,color:"var(--c-text-4)"}}>optional</span></label>
                <input id={inputId("height")} aria-label="Height (m)" type="number" min={0} step={0.1} value={plant?.height||""} onChange={e=>set("height",e.target.value)} placeholder="e.g. 1.5" style={fieldStyle}/>
              </div>
              <div style={fieldWrap}>
                <label htmlFor={inputId("spread")} style={labelStyle}>Spread (m) <span style={{fontWeight:400,color:"var(--c-text-4)"}}>optional</span></label>
                <input id={inputId("spread")} aria-label="Spread (m)" type="number" min={0} step={0.1} value={plant?.spread||""} onChange={e=>set("spread",e.target.value)} placeholder="e.g. 0.6" style={fieldStyle}/>
              </div>
            </div>
          )}

        </>
      )}

      {/* Phase 34 / RES-01 / D-02 — when AI is down, surface a per-CTA note so
          the user sees that the AI lookup will be skipped and they'll get a
          smart-default schedule on add. Echoes the global banner above. */}
      {aiStatus === 'down' && (
        <p style={{fontSize: '0.75rem', color: "var(--c-text-3)", margin: "4px 0 0", fontStyle: "italic"}}>
          AI lookup unavailable — we'll generate a smart-default schedule when you add this plant
        </p>
      )}

      {/* Primary CTA row — always rendered so main-app Cancel + Tip remain visible (Plan 05 regression).
          Primary Add button only active once a name is entered. */}
      <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
        <button
          onClick={handleAdd}
          disabled={!trimmedName || needsTypeChoice}
          style={{background:"var(--c-heading)",color:"#fff",border:"none",borderRadius:20,padding:"9px 22px",fontSize:'0.875rem',fontWeight:600,cursor:(!trimmedName||needsTypeChoice)?"not-allowed":"pointer",opacity:(!trimmedName||needsTypeChoice)?0.5:1}}
        >
          Add plant to Tend
        </button>
        {!compact && onCancel && (
          <button onClick={onCancel} style={{background:"var(--c-border)",color:"var(--c-text-2)",border:"none",borderRadius:20,padding:"9px 18px",fontSize:'0.875rem',cursor:"pointer"}}>Cancel</button>
        )}
        {needsTypeChoice && (
          <span style={{fontSize:'0.75rem',color:"var(--c-text-2)",alignSelf:"center",marginLeft:4}}>
            Pick a plant type above — we'll use that to set up the schedule.
          </span>
        )}
        {!compact && lookupState==="idle" && !needsTypeChoice && aiStatus !== 'down' && (
          <span style={{fontSize:'0.75rem',color:"var(--c-text-4)",alignSelf:"center",marginLeft:4}}>
            {aiReady?"Tip: look up the schedule first for the best results":"Tip: configure an API key to enable AI schedule lookup"}
          </span>
        )}
      </div>
    </div>
  );
}
